import React from 'react'
import { LSPageImmediateVideoCallContainer } from 'src/consumer/components/LiveSessionPage/LiveSessionPageStyles'
import CustomVideoPlaceholder from 'src/consumer/components/LiveSessionPage/components/VideoCall/CustomVideoPlaceholder'
import 'src/consumer/components/LiveSessionPage/VideoCallEnhancedStyles.css'
import { audioManager } from 'src/consumer/utils/audioManager'
import { RTC_PROVIDERS } from '../../providerIds'
import { createRtcProviderRegistry } from '../../providers/rtc'

const rtcProviderRegistry = createRtcProviderRegistry()
const agoraRtcProvider = rtcProviderRegistry.get(RTC_PROVIDERS.agora)

const ToggleState = {
  disabled: 0,
  enabled: 1,
  disabling: 2,
  enabling: 3,
}

export default function PublicLiveAgoraVideoSurface({
  agoraRtcProps,
  agoraRtmProps,
  handleLeaveRoom,
  onUserJoin,
  onUserLeave,
  isFullHeight,
  onCameraClose,
  onCameraOpen,
  onAudioClose,
  onAudioOpen,
  callContainerStyles = {},
  agoraKitStyleProps = {},
  playAudioOnUserJoin = true,
  playAudioOnUserLeave = true,
  onUsernameMapUpdate,
  providerFallback = null,
}) {
  const usernameMapRef = React.useRef({})
  const [uidToUsernameMap, setUidToUsernameMap] = React.useState({})
  const playedUsersRef = React.useRef(new Set())

  const agoraCallbacks = React.useMemo(
    () => ({
      EndCall: () => handleLeaveRoom(),
      'user-joined': (user) => {
        const userKey = `join-${user?.uid}`
        if (playAudioOnUserJoin && !playedUsersRef.current.has(userKey)) {
          playedUsersRef.current.add(userKey)
          audioManager.playJoinSound()
          setTimeout(() => {
            playedUsersRef.current.delete(userKey)
          }, 1000)
        }

        if (onUserJoin) {
          onUserJoin(user)
        }
      },
      'user-left': (user) => {
        const userKey = `leave-${user?.uid}`
        if (playAudioOnUserLeave && !playedUsersRef.current.has(userKey)) {
          playedUsersRef.current.add(userKey)
          audioManager.playLeaveSound()
          setTimeout(() => {
            playedUsersRef.current.delete(userKey)
          }, 1000)
        }

        onUserLeave && onUserLeave(user)
      },
      'rtm-user-joined': (uid, username) => {
        const uidNum = typeof uid === 'string' ? parseInt(uid) : uid
        const uidStr = String(uid)

        usernameMapRef.current[uid] = username
        usernameMapRef.current[uidNum] = username
        usernameMapRef.current[uidStr] = username

        const updatedMap = { ...usernameMapRef.current }
        setUidToUsernameMap(updatedMap)
        onUsernameMapUpdate && onUsernameMapUpdate(updatedMap)
      },
      'rtm-user-left': (uid) => {
        const uidNum = typeof uid === 'string' ? parseInt(uid) : uid
        const uidStr = String(uid)

        delete usernameMapRef.current[uid]
        delete usernameMapRef.current[uidNum]
        delete usernameMapRef.current[uidStr]

        const updatedMap = { ...usernameMapRef.current }
        setUidToUsernameMap(updatedMap)
        onUsernameMapUpdate && onUsernameMapUpdate(updatedMap)
      },
      'local-user-mute-video': (state) => {
        if (state === ToggleState.disabling) {
          onCameraClose && onCameraClose()
        } else if (state === ToggleState.enabling) {
          onCameraOpen && onCameraOpen()
        }
      },
      'local-user-mute-audio': (state) => {
        if (state === ToggleState.disabling) {
          onAudioClose && onAudioClose()
        } else if (state === ToggleState.enabling) {
          onAudioOpen && onAudioOpen()
        }
      },
    }),
    [
      handleLeaveRoom,
      onAudioClose,
      onAudioOpen,
      onCameraClose,
      onCameraOpen,
      onUserJoin,
      onUserLeave,
      onUsernameMapUpdate,
      playAudioOnUserJoin,
      playAudioOnUserLeave,
    ],
  )

  React.useEffect(() => {
    if (agoraRtcProps?.uid === undefined || !agoraRtmProps?.username) {
      return
    }

    const uid = agoraRtcProps.uid
    const uidNum = typeof uid === 'string' ? parseInt(uid) : uid
    const uidStr = String(uid)

    usernameMapRef.current[uid] = agoraRtmProps.username
    usernameMapRef.current[uidNum] = agoraRtmProps.username
    usernameMapRef.current[uidStr] = agoraRtmProps.username

    const updatedMap = { ...usernameMapRef.current }
    setUidToUsernameMap(updatedMap)
    onUsernameMapUpdate && onUsernameMapUpdate(updatedMap)
  }, [agoraRtcProps?.uid, agoraRtmProps?.username, onUsernameMapUpdate])

  const customVideoPlaceholderWrapper = React.useMemo(
    () => (props) =>
      (
        <CustomVideoPlaceholder
          {...props}
          uidToUsernameMap={uidToUsernameMap}
          localRtmUsername={agoraRtmProps?.username}
        />
      ),
    [uidToUsernameMap, agoraRtmProps?.username],
  )

  React.useEffect(() => {
    const makeButtonsClickableAndVisible = () => {
      const container =
        document.querySelector('[data-video-container="left-frame"]') ||
        document.querySelector('.agora-video-container') ||
        document.querySelector('.LSPageImmediateVideoCallContainer')

      if (!container) {
        return
      }

      const buttonContainers = container.querySelectorAll('div[style*="display: flex"]')
      buttonContainers.forEach((btnContainer) => {
        const hasButtons = btnContainer.querySelectorAll('button').length > 0
        if (hasButtons) {
          btnContainer.style.setProperty('visibility', 'visible', 'important')
          btnContainer.style.setProperty('opacity', '1', 'important')
          btnContainer.style.setProperty('display', 'flex', 'important')
          btnContainer.style.setProperty('pointer-events', 'auto', 'important')
          btnContainer.style.setProperty('z-index', '10000', 'important')
          btnContainer.style.setProperty('overflow', 'visible', 'important')
          btnContainer.style.setProperty('clip-path', 'none', 'important')

          const computedPosition = window.getComputedStyle(btnContainer).position
          if (computedPosition === 'static' || !computedPosition) {
            btnContainer.style.setProperty('position', 'absolute', 'important')
            btnContainer.style.setProperty('bottom', '24px', 'important')
            btnContainer.style.setProperty('left', '50%', 'important')
            btnContainer.style.setProperty('transform', 'translateX(-50%)', 'important')
          }
        }
      })

      const buttons = container.querySelectorAll('button')
      buttons.forEach((button) => {
        if (!container.contains(button)) {
          return
        }

        button.style.setProperty('pointer-events', 'auto', 'important')
        button.style.setProperty('touch-action', 'manipulation', 'important')
        button.style.setProperty('cursor', 'pointer', 'important')
        button.style.setProperty('visibility', 'visible', 'important')
        button.style.setProperty('opacity', '1', 'important')
        button.style.setProperty('z-index', '10001', 'important')
      })
    }

    makeButtonsClickableAndVisible()
    const timeout1 = setTimeout(makeButtonsClickableAndVisible, 100)
    const timeout2 = setTimeout(makeButtonsClickableAndVisible, 300)
    const timeout3 = setTimeout(makeButtonsClickableAndVisible, 500)
    const timeout4 = setTimeout(makeButtonsClickableAndVisible, 1000)
    const timeout5 = setTimeout(makeButtonsClickableAndVisible, 2000)
    const interval = setInterval(makeButtonsClickableAndVisible, 2000)

    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
      clearTimeout(timeout3)
      clearTimeout(timeout4)
      clearTimeout(timeout5)
      clearInterval(interval)
    }
  }, [])

  if (!agoraRtcProvider?.CompatibilitySurface) {
    return null
  }

  const AgoraCompatibilitySurface = agoraRtcProvider.CompatibilitySurface

  return (
    <LSPageImmediateVideoCallContainer
      className="hide-on-print-only agora-video-container"
      isFullHeight={isFullHeight}
      style={{
        ...callContainerStyles,
        height: isFullHeight ? '100vh' : '100%',
        maxHeight: isFullHeight ? '100vh' : '100%',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
        flex: isFullHeight ? undefined : '1 1 0',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AgoraCompatibilitySurface
        fallback={providerFallback}
        rtcProps={{
          ...agoraRtcProps,
          CustomVideoPlaceholder: customVideoPlaceholderWrapper,
        }}
        callbacks={agoraCallbacks}
        styleProps={{
          localBtnContainer: {
            padding: window.innerWidth <= 768 ? '12px 14px' : '10px 16px',
            gap: window.innerWidth <= 768 ? '12px' : '10px',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(16px) saturate(150%)',
            WebkitBackdropFilter: 'blur(16px) saturate(150%)',
            borderRadius: window.innerWidth <= 768 ? '24px' : '28px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow:
              '0 4px 16px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.02), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
            width: 'fit-content',
            minWidth: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            position: window.innerWidth <= 768 ? 'fixed' : 'absolute',
            bottom: window.innerWidth <= 768 ? '40px' : '24px',
            left: window.innerWidth <= 768 ? '50%' : '50%',
            transform: 'translateX(-50%)',
            zIndex: window.innerWidth <= 768 ? 10002 : 10000,
            pointerEvents: 'auto',
            ...(window.innerWidth <= 768
              ? {
                  width: 'calc(100% - 40px)',
                  maxWidth: '600px',
                  margin: '0 auto',
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                }
              : {}),
          },
          localBtnStyles: {
            width: window.innerWidth <= 768 ? '52px' : '48px',
            height: window.innerWidth <= 768 ? '52px' : '48px',
            minWidth: window.innerWidth <= 768 ? '52px' : '48px',
            minHeight: window.innerWidth <= 768 ? '52px' : '48px',
            maxWidth: window.innerWidth <= 768 ? '52px' : '48px',
            maxHeight: window.innerWidth <= 768 ? '52px' : '48px',
            borderRadius: window.innerWidth <= 768 ? '14px' : '12px',
            margin: '0',
            padding: '0',
            boxSizing: 'border-box',
            background:
              'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            border: '1.5px solid rgba(255, 255, 255, 0.35)',
            boxShadow:
              '0 4px 16px rgba(0, 0, 0, 0.2), 0 1px 4px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            pointerEvents: 'auto',
            zIndex: 10001,
            position: 'relative',
            flexShrink: 0,
            flexGrow: 0,
            overflow: 'hidden',
          },
          endCall: {
            width: window.innerWidth <= 768 ? '56px' : '52px',
            height: window.innerWidth <= 768 ? '56px' : '52px',
            minWidth: window.innerWidth <= 768 ? '56px' : '52px',
            minHeight: window.innerWidth <= 768 ? '56px' : '52px',
            maxWidth: window.innerWidth <= 768 ? '56px' : '52px',
            maxHeight: window.innerWidth <= 768 ? '56px' : '52px',
            borderRadius: window.innerWidth <= 768 ? '16px' : '14px',
            padding: '0',
            margin: '0',
            boxSizing: 'border-box',
            background:
              'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 1) 100%)',
            backgroundColor: 'rgba(239, 68, 68, 0.95)',
            border: '1.5px solid rgba(255, 255, 255, 0.4)',
            boxShadow:
              '0 6px 24px rgba(239, 68, 68, 0.6), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.35)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            pointerEvents: 'auto',
            zIndex: 10001,
            position: 'relative',
            cursor: 'pointer',
            flexShrink: 0,
            flexGrow: 0,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          ...(agoraKitStyleProps || {}),
          UIKitContainer: {
            background: '#ffffff',
            border: 'none',
            boxShadow: 'none',
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            ...(agoraKitStyleProps?.UIKitContainer || {}),
          },
          usernameText: {
            background:
              'linear-gradient(135deg, rgba(139, 92, 246, 0.85) 0%, rgba(99, 102, 241, 0.85) 100%)',
            backdropFilter: 'blur(12px) saturate(180%)',
            WebkitBackdropFilter: 'blur(12px) saturate(180%)',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: '16px',
            boxShadow:
              '0 4px 16px rgba(139, 92, 246, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            letterSpacing: '0.3px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            position: 'absolute',
            top: '12px',
            left: '12px',
            right: 'auto',
            margin: '0',
            width: 'auto',
            maxWidth: 'calc(100% - 24px)',
            minWidth: 'fit-content',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(agoraKitStyleProps?.usernameText || {}),
          },
        }}
        rtmProps={agoraRtmProps}
      />
    </LSPageImmediateVideoCallContainer>
  )
}
